import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import IdChip from './IdChip';
import { useEntityTrail, useRegisterTrailOrigin } from '../../context/EntityTrailContext';
import { collapseTrail } from '../../utils/entityTrail';

/**
 * Detail-page breadcrumb. With no incoming trail it renders the static
 * `parentLabel / current [chip]` shape. Arriving via a `TrailLink`/
 * `navigateWithTrail` hop from another entity's detail page instead renders
 * the full recorded path, e.g. `Deudas / D-0012 / Juan Pérez` — each earlier
 * crumb links back to itself with the trail truncated to that point.
 *
 * Also registers this page's own identity as the trail's current "origin",
 * so any `TrailLink` / `navigateWithTrail` call elsewhere on the page (tab
 * tables, `*Info` links, the header name link, ...) knows what to append
 * when the user navigates on to another entity. No other wiring is needed
 * on the 6 detail screens beyond rendering this component.
 */
function DetailBreadcrumb({ parentLabel, parentTo, current, chip }) {
	const location = useLocation();
	const { trail } = useEntityTrail();

	const currentLabel = typeof current === 'string' ? current : '';
	useRegisterTrailOrigin(
		currentLabel
			? {
				root: { label: parentLabel, to: parentTo },
				current: { label: currentLabel, to: location.pathname },
			}
			: null,
	);

	if (trail.length === 0) {
		return (
			<div className='tw:mb-4 tw:flex tw:items-center tw:gap-1.5 tw:text-[12.5px] tw:text-ink-3'>
				<Link to={parentTo} className='tw:text-ink-3 tw:no-underline tw:hover:text-ink-1'>
					{parentLabel}
				</Link>
				<span>/</span>
				<span className='tw:text-ink-1'>{current}</span>
				{chip && <IdChip>{chip}</IdChip>}
			</div>
		);
	}

	return (
		<div className='tw:mb-4 tw:flex tw:items-center tw:gap-1.5 tw:text-[12.5px] tw:text-ink-3'>
			{collapseTrail(trail).map((crumb, i) =>
				crumb.ellipsis ? (
					<Fragment key={`ellipsis-${i}`}>
						<span className='tw:text-ink-3'>…</span>
						<span>/</span>
					</Fragment>
				) : (
					<Fragment key={crumb.to}>
						<Link
							to={crumb.to}
							state={{ trail: trail.slice(0, crumb.index) }}
							className='tw:text-ink-3 tw:no-underline tw:hover:text-ink-1'
						>
							{crumb.label}
						</Link>
						<span>/</span>
					</Fragment>
				),
			)}
			<span className='tw:text-ink-1'>{current}</span>
			{chip && <IdChip>{chip}</IdChip>}
		</div>
	);
}

DetailBreadcrumb.propTypes = {
	parentLabel: PropTypes.node.isRequired,
	parentTo: PropTypes.string.isRequired,
	current: PropTypes.node.isRequired,
	chip: PropTypes.node,
};

export default DetailBreadcrumb;
